import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ConstantsLagoon } from '../../../../../../../../../../common/ConstantsLagoon';
import { useIsOverflow } from '../../../../../../../../../../hooks/useIsOverflow';
import { Address } from '../../../../../../../../../../models/ProviderDetails';
import { useChipStore } from '../../../../../../../../../../store/useChipStore';
import { ChipState } from '../../../../../../../../../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../../../../../../../../../utils/featureSuppress';
import { isVirtualVisitOnlyAddress } from '../../../../../../../../../../utils/virtualVisitOnly';
import { StyledMiniCompareCardAddressDiv } from '../../../../../utility/compareDrawer.styled';
import { getDisplayedAddressText, isAddressPresent } from './addressUtility';

type Props = {
  address: Address;
};

export const MiniCompareCardAddress = ({ address }: Props) => {
  const { t } = useTranslation();
  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
    }))
  );
  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );
  const shouldShowVirtualVisitAddress = (
    addressLine: string,
    suppressVirtualCareQuickCare: boolean
  ) =>
    suppressVirtualCareQuickCare &&
    isVirtualVisitOnlyAddress(chipValue, addressLine);
  const isAttributePresent = isAddressPresent(address);
  const displayedAddress =
    !shouldShowVirtualVisitAddress(
      address?.line[0],
      isVirtualCareQuickCareSuppressed
    ) && t(getDisplayedAddressText(address, isAttributePresent));
  const containerRef = useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(containerRef);

  const MiniCompareCareAddressAttribute = (
    <StyledMiniCompareCardAddressDiv
      cssProps={{ isOverflow }}
      ref={containerRef}
    >
      <Text
        css={{ cursor: 'auto' }}
        data-auto-testid="data-card-address-text"
        data-testid="data-card-address-text"
        fontSize="sm"
      >
        {displayedAddress}
      </Text>
    </StyledMiniCompareCardAddressDiv>
  );

  return (
    <React.Fragment>
      <Flex alignItems="center">
        {!shouldShowVirtualVisitAddress(
          address?.line[0],
          isVirtualCareQuickCareSuppressed
        ) && (
          <IconSymbol
            className="location-icon"
            color="$core.color.neutral.80"
            css={{
              marginBottom: '0.25rem',
            }}
            data-auto-testid="data-card-location-on-icon"
            data-testid="data-card-location-on-icon"
            icon="location_on"
            size="18px"
          />
        )}
        {isOverflow ? (
          <Tooltip
            asChild={false}
            content={displayedAddress}
            css={{
              'abyss-tooltip-trigger': {
                lineHeight: 0.8,
                // '> div': { height: 'auto' },
                '.abyss-text-root': {
                  fontWeight: 400,
                  color: '$core.color.neutral.90',
                  fontFamily: '$primary',
                },
              },
            }}
            position="top"
            role="tooltip"
          >
            {MiniCompareCareAddressAttribute}
          </Tooltip>
        ) : (
          MiniCompareCareAddressAttribute
        )}
      </Flex>
    </React.Fragment>
  );
};
